/**
 * Détermination des passages d'un POI — EF-CAL-006.
 *
 * Un parcours peut longer un même POI plusieurs fois : boucle, aller-retour,
 * détour vers un checkpoint. Un passage est un minimum local de la distance
 * entre le POI et la trace de calcul.
 *
 * Les cinq étapes s'exécutent dans cet ordre. Elles rendent le résultat
 * indépendant du bruit des coordonnées, qui produirait sinon des dizaines de
 * minima parasites par POI.
 */

import { distance, projectOnSegment, type Coordinate } from './geodesy';
import type { SpatialIndex } from './spatial-index';
import { coordinateAt, progressionAt, type ComputedTrack } from './track';

/** Plancher de proéminence, en mètres. Écarte le bruit de coordonnées. */
export const PROMINENCE_FLOOR_METERS = 50;

/** Part proportionnelle de la proéminence requise. */
export const PROMINENCE_RATIO = 0.1;

/** Deux candidats plus proches que ce seuil sont fusionnés (étape 4). */
export const PASSAGE_MERGE_METERS = 500;

/** Marge de départage entre deux projections de distance équivalente. */
export const PROJECTION_TIE_METERS = 1;

/** Portée de passage minimale et maximale, en mètres (invariants EF-RBK-005). */
export const MIN_PASSAGE_REACH_METERS = 100;
export const MAX_PASSAGE_REACH_METERS = 5000;

export interface PassageComputation {
  /** Progression de la projection retenue, en mètres. */
  readonly progressionMeters: number;
  /** Distance à la polyligne pour ce passage, en mètres. */
  readonly distanceToRouteMeters: number;
}

/** Proéminence requise pour un candidat situé à `distanceMeters` du POI. */
export function requiredProminence(distanceMeters: number): number {
  return Math.max(PROMINENCE_FLOOR_METERS, PROMINENCE_RATIO * distanceMeters);
}

/**
 * Calcule tous les passages d'un POI. Le résultat est ordonné par progression
 * croissante et contient toujours au moins un élément dès que la trace est non
 * vide : le minimum global est conservé même lorsqu'il dépasse la portée.
 */
export function computePassages(
  track: ComputedTrack,
  index: SpatialIndex,
  poi: Coordinate,
  reachMeters: number,
): readonly PassageComputation[] {
  if (track.count === 0) return [];
  if (track.count === 1) {
    return [{ progressionMeters: 0, distanceToRouteMeters: distance(poi, coordinateAt(track, 0)) }];
  }

  const queryRadius = reachMeters + requiredProminence(reachMeters);
  const distances = sampleDistances(track, index, poi, queryRadius);
  if (distances.size === 0) return [];

  const bound = queryRadius;
  const distanceAt = (i: number): number => {
    if (i < 0 || i >= track.count) return Number.POSITIVE_INFINITY;
    const known = distances.get(i);
    return known === undefined ? bound : known;
  };

  // Étape 2 — candidats et proéminence.
  const candidateIndices: number[] = [];
  let globalMinIndex = -1;
  let globalMinDistance = Number.POSITIVE_INFINITY;

  for (const [i, value] of distances) {
    if (value < globalMinDistance) {
      globalMinDistance = value;
      globalMinIndex = i;
    }
    if (value > distanceAt(i - 1) || value > distanceAt(i + 1)) continue;
    if (!isProminent(distanceAt, track.count, i, value)) continue;
    candidateIndices.push(i);
  }

  // Le minimum global est retenu en toute hypothèse, même lorsque le filtre de
  // proéminence l'a écarté — cas d'un minimum situé au bord de la trace.
  if (globalMinIndex >= 0 && !candidateIndices.includes(globalMinIndex)) {
    candidateIndices.push(globalMinIndex);
  }
  if (candidateIndices.length === 0) return [];

  candidateIndices.sort((a, b) => a - b);

  // Étape 3 — affinage sur les deux segments adjacents.
  const refined = candidateIndices.map((i) => refineCandidate(track, poi, i));

  // Étape 4 — fusion de proximité, par ancrage.
  const merged = mergeByAnchor(refined);

  // Étape 5 — portée, le minimum global étant toujours conservé.
  return applyReach(merged, reachMeters);
}

function sampleDistances(
  track: ComputedTrack,
  index: SpatialIndex,
  poi: Coordinate,
  queryRadius: number,
): Map<number, number> {
  let radius = queryRadius;
  const limit = Math.max(queryRadius, track.lengthMeters * 2, 1);

  for (;;) {
    const candidates = index.query(poi, radius);
    if (candidates.length > 0) {
      const distances = new Map<number, number>();
      for (const i of candidates) distances.set(i, distance(poi, coordinateAt(track, i)));
      return distances;
    }
    if (radius >= limit) break;
    radius = Math.min(limit, radius * 4);
  }

  // Repli exhaustif : la trace est courte ou le POI très éloigné.
  const distances = new Map<number, number>();
  for (let i = 0; i < track.count; i += 1) distances.set(i, distance(poi, coordinateAt(track, i)));
  return distances;
}

/**
 * La proéminence d'un candidat est la plus petite des deux remontées, à gauche
 * et à droite, avant que la distance ne redescende sous la valeur du candidat.
 * La marche s'arrête dès que le seuil est atteint : il suffit de savoir s'il
 * l'est, non de mesurer la remontée exacte.
 */
function isProminent(
  distanceAt: (i: number) => number,
  count: number,
  candidate: number,
  value: number,
): boolean {
  const required = requiredProminence(value);
  return (
    sideReaches(distanceAt, count, candidate, -1, value, required) &&
    sideReaches(distanceAt, count, candidate, 1, value, required)
  );
}

function sideReaches(
  distanceAt: (i: number) => number,
  count: number,
  candidate: number,
  direction: -1 | 1,
  value: number,
  required: number,
): boolean {
  let highest = value;
  let i = candidate + direction;
  for (;;) {
    if (i < 0 || i >= count) return highest - value >= required;
    const d = distanceAt(i);
    if (d < value) return highest - value >= required;
    if (d > highest) highest = d;
    if (highest - value >= required) return true;
    i += direction;
  }
}

/**
 * Étape 3. La trace n'étant échantillonnée que tous les 100 m, le minimum réel
 * se situe entre deux points. La distance et la progression exactes sont
 * obtenues en projetant le POI sur les deux segments adjacents et en retenant
 * la plus petite des deux projections.
 */
function refineCandidate(track: ComputedTrack, poi: Coordinate, index: number): PassageComputation {
  const projections: PassageComputation[] = [];
  if (index - 1 >= 0) projections.push(projectOnSegmentAt(track, poi, index - 1));
  if (index + 1 < track.count) projections.push(projectOnSegmentAt(track, poi, index));

  let best = projections[0];
  if (best === undefined) {
    return {
      progressionMeters: progressionAt(track, index),
      distanceToRouteMeters: distance(poi, coordinateAt(track, index)),
    };
  }

  for (let i = 1; i < projections.length; i += 1) {
    const candidate = projections[i];
    if (candidate === undefined) continue;
    if (isBetterProjection(candidate, best)) best = candidate;
  }
  return best;
}

function projectOnSegmentAt(track: ComputedTrack, poi: Coordinate, from: number): PassageComputation {
  const projection = projectOnSegment(poi, coordinateAt(track, from), coordinateAt(track, from + 1));
  return {
    progressionMeters: progressionAt(track, from) + projection.along,
    distanceToRouteMeters: projection.distance,
  };
}

/**
 * Règle de projection de EF-CAL-006 : à distance minimale identique à 1 m près,
 * celle de plus petite progression est retenue. Cette règle rend l'ordre du
 * roadbook déterministe et reproductible.
 */
function isBetterProjection(candidate: PassageComputation, current: PassageComputation): boolean {
  const delta = candidate.distanceToRouteMeters - current.distanceToRouteMeters;
  if (delta < -PROJECTION_TIE_METERS) return true;
  if (delta > PROJECTION_TIE_METERS) return false;
  return candidate.progressionMeters < current.progressionMeters;
}

/**
 * Étape 4. Le premier candidat ouvre un passage et en devient l'ancre ; les
 * suivants le rejoignent tant qu'ils sont à moins de 500 m de lui, et le
 * premier au-delà ouvre le passage suivant.
 */
function mergeByAnchor(candidates: readonly PassageComputation[]): readonly PassageComputation[] {
  const sorted = [...candidates].sort((a, b) => a.progressionMeters - b.progressionMeters);
  const passages: PassageComputation[] = [];

  let anchorProgression = Number.NaN;
  let best: PassageComputation | undefined;

  for (const candidate of sorted) {
    if (best === undefined || candidate.progressionMeters - anchorProgression >= PASSAGE_MERGE_METERS) {
      if (best !== undefined) passages.push(best);
      anchorProgression = candidate.progressionMeters;
      best = candidate;
      continue;
    }
    if (isBetterProjection(candidate, best)) best = candidate;
  }
  if (best !== undefined) passages.push(best);

  return passages;
}

/**
 * Étape 5. Les passages dont la distance dépasse la portée sont écartés, sauf
 * le minimum global, toujours conservé afin qu'un POI éloigné de la trace
 * conserve une position.
 */
function applyReach(
  passages: readonly PassageComputation[],
  reachMeters: number,
): readonly PassageComputation[] {
  if (passages.length === 0) return passages;

  let globalMin = passages[0];
  if (globalMin === undefined) return [];
  for (const passage of passages) {
    if (passage.distanceToRouteMeters < globalMin.distanceToRouteMeters) globalMin = passage;
  }

  const kept = passages.filter((p) => p.distanceToRouteMeters <= reachMeters);
  if (kept.length === 0) return [globalMin];
  if (!kept.includes(globalMin)) {
    return [...kept, globalMin].sort((a, b) => a.progressionMeters - b.progressionMeters);
  }
  return kept;
}

/**
 * Passage unique d'un POI créé par progression (EF-POI-004) : la distance à la
 * trace vaut alors 0 et aucune des cinq étapes n'est appliquée.
 */
export function passageAtProgression(progressionMeters: number): PassageComputation {
  return { progressionMeters, distanceToRouteMeters: 0 };
}
